<script setup lang="ts">
    import { ref, watch } from 'vue'

    /**
     * A QR code drawn as inline SVG, with `qrcode` loaded on first use. The
     * code stays dark on a white ground whatever the theme: a scanner reads
     * the contrast, and not every one reads it inverted.
     */
    const props = defineProps<{
        value: string
        /** What the code holds, for a screen reader. */
        label: string
    }>()

    const svg = ref('')
    watch(
        () => props.value,
        async (value) => {
            if (!value) {
                svg.value = ''
                return
            }
            const { default: QRCode } = await import('qrcode')
            svg.value = await QRCode.toString(value, {
                type: 'svg',
                margin: 1,
            })
        },
        { immediate: true },
    )
</script>

<template>
    <!-- The markup comes from `qrcode`, which encodes the value as path data:
         nothing of the value reaches the SVG as text. -->
    <div class="pk-qr-code" role="img" :aria-label="label" v-html="svg" />
</template>

<style lang="scss">
    .pk-qr-code {
        width: var(--spacing-176);
        aspect-ratio: 1;
        padding: var(--spacing-8);
        background-color: var(--color-white);
        border: var(--spacing-px) solid var(--color-surface-3);
        border-radius: var(--rounded-lg);

        & > svg {
            display: block;
            width: 100%;
            height: 100%;
        }
    }
</style>
